import { withAITracking } from "@microsoft/applicationinsights-react-js";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Trans } from "react-i18next";

import useDemographics from "/b2c/query/useDemographics.b2c";
import { Link, Loading } from "/component/base";
import List from "/component/base/List";
import Text from "/component/base/Text";
import MainContent from "/component/layout/MainContent";
import { useUserAccount } from "/component/layout/MainLayout/useUserAccount";
import { useContactInfo, useTranslation } from "/hook";
import { useAnalytics } from "/hook/useAnalytics/useAnalytics";
import { layout } from "/styles";
import { date, formatText } from "/util";
import { reactPlugin } from "/util/appInsights.util";
import { isMobileOrTabletDevice } from "/util/device.util";

import {
  AddressesSubtitle,
  AddressText,
  InfoListItem,
  ListContainer,
  PersonalInformationSubtitle,
} from "./PersonalInformationB2C.styles";
import { buildAddressValue, PersonalAddress } from "./PersonalInformationB2C.utils";

const PersonalInformation = () => {
  const { data: patientB2c, loading: patientLoading } = useDemographics();
  const patientData = patientB2c?.patient?.demographics;
  const { data: accountAzure, loading: accountLoading } = useUserAccount();
  const accountData = accountAzure?.viewer?.user.account;
  const isMobileOrTablet = isMobileOrTabletDevice();
  const { t } = useTranslation("personalInformation");
  const { recordAnalyticsEvent } = useAnalytics();
  const { contactByName } = useContactInfo();
  const callCenterPhone = contactByName("summitCallCenter")?.phoneNumber;

  useEffect(() => {
    recordAnalyticsEvent("viewProfile");
  }, [recordAnalyticsEvent]);

  if (patientLoading || accountLoading) {
    return (
      <MainContent parentPage="account" pageTitle={t("title")}>
        <Helmet>
          <title>{t("title")}</title>
        </Helmet>

        <Loading />
      </MainContent>
    );
  }

  if (patientData || accountData) {
    const birthDate = date.removeOffset(patientData?.dateOfBirth);
    const details = [
      { label: t("firstName"), value: patientData?.firstName || accountData?.firstName },
      { label: t("lastName"), value: patientData?.lastName || accountData?.lastName },
    ];

    if (birthDate) {
      details.push({
        label: t("dateOfBirth"),
        value: date.formatDate(birthDate, "MMMM d, yyyy"),
      });
    }

    // Only displaying the user's home address for now. This could be
    // updated in the future to show multiple user addresses.
    const address: PersonalAddress = {
      address1: patientData?.address1 || accountData?.line1,
      address2: patientData?.address2 || accountData?.line2,
      city: patientData?.city || accountData?.city,
      state: patientData?.state || accountData?.state,
      zipCode: patientData?.zipCode || accountData?.postalCode,
    };
    const addresses = [{ label: t("home") }].map(({ label }) => ({
      label,
      stringValue: buildAddressValue(address),
    }));

    return (
      <MainContent parentPage="account" pageTitle={t("title")}>
        <Helmet>
          <title>{t("title")}</title>
        </Helmet>

        <PersonalInformationSubtitle color="textSecondary">
          <Trans
            components={{
              anchor: isMobileOrTablet ? (
                <Link.Anchor href={`tel:${callCenterPhone}`} variant="secondary" />
              ) : (
                <Text variant="body1Bold" />
              ),
            }}
            i18nKey="subtitle"
            t={t}
            tOptions={{ context: callCenterPhone ? "hasNumber" : undefined }}
            values={{ phoneNumber: formatText.parsePhone(callCenterPhone) }}
          />
        </PersonalInformationSubtitle>
        <ListContainer>
          <List
            as="Divs"
            data={details}
            showChevron={false}
            hideLastChildBorder
            renderItem={({ label, value }) => (
              <InfoListItem>
                <Text>{label}</Text>
                <Text>{value}</Text>
              </InfoListItem>
            )}
          />
        </ListContainer>
        <AddressesSubtitle variant="body1Bold" color="textSecondary">
          {t("addresses")}
        </AddressesSubtitle>
        <ListContainer>
          <List
            as="Divs"
            data={addresses}
            showChevron={false}
            hideLastChildBorder
            renderItem={({ label, stringValue }) => (
              <InfoListItem css={layout.margin("skip", "condensed", "skip", "skip")}>
                <Text>{label}</Text>
                <AddressText>{stringValue}</AddressText>
              </InfoListItem>
            )}
          />
        </ListContainer>
      </MainContent>
    );
  }

  // TODO: Handle errors
  return <Text>{t("personalInfoError")}</Text>;
};

export default withAITracking(reactPlugin, PersonalInformation, undefined, "ai-tracking");
